import {
  AccountCenterInteractedChangeOfAddressSegmentProperties,
  AccountCenterInteractedChangeOfPaymentModeSegmentProperties,
  AccountCenterInteractedSegmentProperties,
  AdvisorQuoteToolSegmentProperties,
  ApplicationEditedSegmentProperties,
  CommissionChangeSegmentProperties,
  ConceptIllustrationCalculatedSegmentProperties,
  ConceptIllustrationDownloadSupplementalIllustrationSegmentProperties,
  DefaultAdvisorSiteSegmentProperties,
  DuplicateApplicationSegmentProperties,
  EmailSurveyResponseProperties,
  GenericLinkActionSegmentProperties,
  getSegmentEventAction,
  LinkSentSegmentProperties,
  QuotePageClearFormSegmentProperties,
  QuotePageSearchSegmentProperties,
  SegmentEventAction,
  ViewApplicationSegmentProperties,
  ViewPolicySegmentProperties,
  VSTInteractedSegmentProperties,
} from './SegmentInterfaces';
import { AgentController } from '../../../../coverpath-agent/public/js/types/Types';
import { Utils } from '../../../../common/js/Utils';
import { CoverpathControllerInterface } from '../../interfaces/controller/CoverpathControllerInterface';
import { Policy } from '../../../../common/js/customer/Policy';
import { RateClasses } from '../../../../common/js/static/RateClasses';
import { RiderType } from '../../../../common/js/static/Product';
import { Application } from '../../../../common/js/customer/Application';
import { BaseQuote } from '../../../../common/js/quote/BaseQuote';
import { CoverpathPolicy } from '../../../classes/coverpath_subclasses/CoverpathPolicy';
import { OwnerCategory } from '../../../js/interfaces/CoverpathTypes';

export class AgentSiteSegmentUtils {
  static createDefaultSegmentProperties(controller: AgentController): DefaultAdvisorSiteSegmentProperties {
    const defaultProperties: DefaultAdvisorSiteSegmentProperties = {
      environment: controller.amorphic.config.nconf.get('environment'),
      producer_id:
        controller.isTeleInterviewerLoggedIn || !controller.getAgent()
          ? controller.loggedInPrincipal.user.producerId
          : controller.getAgent().producerId,
      proxy_id:
        controller.hasProxyLoggedIn() || controller.hasWholesaleProxyLoggedIn()
          ? controller.loggedInPrincipal.user.producerId
          : null,
    };

    return defaultProperties;
  }

  static createGenericLinkActionSegmentProperties(
    controller: CoverpathControllerInterface,
    action: SegmentEventAction
  ): GenericLinkActionSegmentProperties {
    const defaultProperties = AgentSiteSegmentUtils.createDefaultSegmentProperties(controller as AgentController);
    const properties: GenericLinkActionSegmentProperties = {
      ...defaultProperties,
      value: action,
    };

    return properties;
  }

  static createQuotePageSearchSegmentProperties(
    controller: CoverpathControllerInterface,
    action: SegmentEventAction,
    customerId?: string
  ): QuotePageSearchSegmentProperties {
    const defaultProperties = AgentSiteSegmentUtils.createDefaultSegmentProperties(controller as AgentController);
    const properties: QuotePageSearchSegmentProperties = {
      ...defaultProperties,
      action: action,
      customer_id: customerId ? customerId : null,
    };

    return properties;
  }

  static createQuotePageClearFormSegmentProperties(
    controller: CoverpathControllerInterface
  ): QuotePageClearFormSegmentProperties {
    const defaultProperties = AgentSiteSegmentUtils.createDefaultSegmentProperties(controller as AgentController);
    const properties: QuotePageClearFormSegmentProperties = {
      ...defaultProperties,
      action: SegmentEventAction.CLEAR_FORM,
    };
    return properties;
  }

  static createLinkSentSegmentProperties(
    controller: CoverpathControllerInterface,
    application: Application,
    sourceType: string,
    _dividendInterestAdjustment: number,
    _showPremiumToClient: boolean
  ): LinkSentSegmentProperties {
    const defaultProperties = AgentSiteSegmentUtils.createDefaultSegmentProperties(controller as AgentController);
    const policy: CoverpathPolicy = application.primaryPolicy as CoverpathPolicy;
    const secondaryPolicy: CoverpathPolicy = application.secondaryPolicy as CoverpathPolicy;
    const properties: LinkSentSegmentProperties = {
      ...defaultProperties,
      link_type: application.isAgentAssistant
        ? SegmentEventAction.ADVISOR_ASSISTED_APP_LINK_SENT
        : SegmentEventAction.PERSONALIZED_LINK_SENT,
      customer_id: Utils.extractPersistorObjectDbId(application.customer.__id__.toString()),
      primary_term_length: policy.term,
      primary_face_amount: policy.faceAmount,
      primary_product: policy.product,
      secondary_term_length: secondaryPolicy ? secondaryPolicy.term : null,
      secondary_face_amount: secondaryPolicy ? secondaryPolicy.faceAmount : null,
      secondary_product: secondaryPolicy ? secondaryPolicy.product : null,
      insured_age: policy.insured.person.getInsuranceAge(),
      owner_state: policy.ownerPersonResidentialAddress ? policy.ownerPersonResidentialAddress.state : null,
      insured_gender: policy.insured.person.gender === '1' ? 'M' : 'F',
      payment_frequency: policy.paymentFrequency,
      rate_class: secondaryPolicy
        ? //for joint app, it would be the best rate class code, which is on the secondary quote
          RateClasses[policy.application.quote.secondaryQuote.healthClass].code
        : RateClasses[policy.application.quote.healthClass].code,
      gir_face_amount: policy.getRider(RiderType.GIR) ? policy.getRider(RiderType.GIR).amount : null,
      rtr_face_amount: policy.getRider(RiderType.RTR) ? policy.getRider(RiderType.RTR).amount : null,
      primary_waiver: policy.waiver,
      secondary_waiver: secondaryPolicy ? secondaryPolicy.waiver : null,
      quote_use_case: this.getPolicyUseCase(policy).toString(),
      owner_equals_insured: policy.isOwnerSameAsInsured(),
      owner_entity_name: policy.ownerEntity ? policy.ownerEntity.getFullNameOfOwner() : null,
      application_source_type: sourceType,
      dividendInterestAdjustment: _dividendInterestAdjustment,
      showPremiumToClient: _showPremiumToClient,
    };

    return properties;
  }

  static getPolicyUseCase(policy: Policy): OwnerCategory {
    let result: OwnerCategory = null;
    if (policy.ownerEntity && policy.ownerEntity.type === 'trust') {
      result = OwnerCategory.TRUST;
    } else if (policy.ownerEntity) {
      result = OwnerCategory.BUSINESS;
    } else if (policy.isJointOwnership()) {
      result = OwnerCategory.JOINT;
    } else {
      result = OwnerCategory.INDIVIDUAL;
    }
    return result;
  }

  static createAdvisorQuoteToolSegmentProperties(
    controller: CoverpathControllerInterface,
    quote: BaseQuote,
    action: SegmentEventAction
  ): AdvisorQuoteToolSegmentProperties {
    const defaultProperties = AgentSiteSegmentUtils.createDefaultSegmentProperties(controller as AgentController);
    const properties: AdvisorQuoteToolSegmentProperties = {
      ...defaultProperties,
      action: action,
      application_number: quote.customer.application.applicationNumber,
      primary_term_length: quote.term,
      primary_face_amount: quote.faceAmount,
      primary_product: quote.product,
      secondary_term_length: quote.secondaryQuote ? quote.secondaryQuote.term : null,
      secondary_face_amount: quote.secondaryQuote ? quote.secondaryQuote.faceAmount : null,
      secondary_product: quote.secondaryQuote ? quote.secondaryQuote.product : null,
      primary_waiver: quote.waiverOfPremium,
      secondary_waiver: quote.secondaryQuote ? quote.secondaryQuote.waiverOfPremium : null,
      gir_face_amount: quote.getRider(RiderType.GIR) ? quote.getRider(RiderType.GIR).amount : null,
      rtr_face_amount: quote.getRider(RiderType.RTR) ? quote.getRider(RiderType.RTR).amount : null,
    };

    return properties;
  }

  static createCommissionChangeSegmentProperties(
    controller: CoverpathControllerInterface,
    action: string,
    numOfAgents: number,
    fycPercentForPrimary: number,
    rycPercentForPrimary: number
  ): CommissionChangeSegmentProperties {
    const defaultProperties = AgentSiteSegmentUtils.createDefaultSegmentProperties(controller as AgentController);
    const properties: CommissionChangeSegmentProperties = {
      ...defaultProperties,
      action: getSegmentEventAction(action),
      application_number: controller.customer.application ? controller.customer.application.applicationNumber : null,
      number_of_agents: numOfAgents,
      FYC_percentage_for_primary: fycPercentForPrimary,
      RYC_percentage_for_primary: rycPercentForPrimary,
    };

    return properties;
  }

  static createVSTInteractedSegmentProperties(
    controller: CoverpathControllerInterface,
    action: SegmentEventAction,
    value: string
  ): VSTInteractedSegmentProperties {
    const defaultProperties = AgentSiteSegmentUtils.createDefaultSegmentProperties(controller as AgentController);
    const properties: VSTInteractedSegmentProperties = {
      ...defaultProperties,
      action: action,
      value: value,
    };

    return properties;
  }

  static createViewApplicationSegmentProperties(
    controller: CoverpathControllerInterface,
    action: SegmentEventAction,
    source: string,
    applicationId?: string
  ): ViewApplicationSegmentProperties {
    const defaultProperties = AgentSiteSegmentUtils.createDefaultSegmentProperties(controller as AgentController);
    const properties: ViewApplicationSegmentProperties = {
      ...defaultProperties,
      action: action,
      source: source,
      application_id: applicationId
        ? applicationId
        : Utils.extractPersistorObjectDbId(controller.customer.application.__id__.toString()),
    };

    return properties;
  }

  static createViewPolicySegmentProperties(
    controller: CoverpathControllerInterface,
    action: SegmentEventAction,
    source: string,
    channel: string,
    product: string,
    productType: string
  ): ViewPolicySegmentProperties {
    const defaultProperties = AgentSiteSegmentUtils.createDefaultSegmentProperties(controller as AgentController);
    const properties: ViewPolicySegmentProperties = {
      ...defaultProperties,
      action: action,
      source: source,
      channel,
      product,
      productType,
    };

    return properties;
  }
  static createDuplicateApplicationSegmentProperties(
    controller: CoverpathControllerInterface,
    action: string,
    oldApplicationNumber: string,
    newApplicationNumber: string,
    dupLocation: string
  ) {
    const defaultProperties = AgentSiteSegmentUtils.createDefaultSegmentProperties(controller as AgentController);

    const properties: DuplicateApplicationSegmentProperties = {
      ...defaultProperties,
      action: action,
      original_application_number: oldApplicationNumber,
      new_application_number: newApplicationNumber,
      location: dupLocation,
    };

    return properties;
  }

  static createAccountCenterInteractedSegmentProperties(
    controller: CoverpathControllerInterface,
    action: SegmentEventAction,
    value: string
  ): AccountCenterInteractedSegmentProperties {
    const defaultProperties = AgentSiteSegmentUtils.createDefaultSegmentProperties(controller as AgentController);
    const properties: AccountCenterInteractedSegmentProperties = {
      ...defaultProperties,
      action: action,
      value: value,
    };

    return properties;
  }

  static createApplicationEditedSegmentProperties(
    controller: CoverpathControllerInterface,
    action: string,
    sourceApplicationNumber: string,
    newApplicationNumber: string,
    location: string
  ) {
    const defaultProperties = AgentSiteSegmentUtils.createDefaultSegmentProperties(controller as AgentController);
    const properties: ApplicationEditedSegmentProperties = {
      ...defaultProperties,
      action,
      source_application_number: sourceApplicationNumber,
      new_application_number: newApplicationNumber,
      location,
    };

    return properties;
  }

  static createConceptIllustrationDownloadSupplementalIllustrationProperties(
    controller: CoverpathControllerInterface,
    action: string,
    product: string,
    isSupplemental: boolean
  ) {
    const defaultProperties = AgentSiteSegmentUtils.createDefaultSegmentProperties(controller as AgentController);
    const properties: ConceptIllustrationDownloadSupplementalIllustrationSegmentProperties = {
      ...defaultProperties,
      action,
      product,
      isSupplemental,
    };

    return properties;
  }

  static createConceptIllustrationCalculatedSegmentProperties(
    controller: CoverpathControllerInterface,
    action: string,
    CIProperties: ConceptIllustrationCalculatedSegmentProperties
  ) {
    // remove the default values so they can be overwritten
    delete CIProperties.proxy_id;
    delete CIProperties.environment;
    delete CIProperties.producer_id;
    const defaultProperties = AgentSiteSegmentUtils.createDefaultSegmentProperties(controller as AgentController);
    const properties: ConceptIllustrationCalculatedSegmentProperties = {
      ...CIProperties,
      ...defaultProperties,

      action,
    };

    return properties;
  }

  static createAccountCenterInteractedChangeOfPaymentModeSegmentProperties(
    controller: CoverpathControllerInterface,
    action: SegmentEventAction,
    currentPayment,
    targetFrequency
  ): AccountCenterInteractedChangeOfPaymentModeSegmentProperties {
    const defaultProperties = AgentSiteSegmentUtils.createDefaultSegmentProperties(controller as AgentController);
    const currentPaymentDisplay = {
      type: currentPayment.type,
      price: currentPayment.price,
    };
    const targetPaymentDisplay = {
      type: targetFrequency.type,
      price: targetFrequency.price,
      annualizedPremium: targetFrequency.description,
      isLess: targetFrequency.isLess,
      nextPaymentDate: targetFrequency.nextPaymentDate,
      nextPaidToDate: targetFrequency.nextPaidToDate,
      proratedAmount: targetFrequency.proratedAmount,
      prorationFactor: targetFrequency.prorationFactor,
    };

    return {
      ...defaultProperties,
      action: action,
      currentPayment: currentPaymentDisplay,
      targetPayment: targetPaymentDisplay,
    };
  }

  static createAccountCenterInteractedChangeOfAddressSegmentProperties(
    controller: CoverpathControllerInterface,
    action: SegmentEventAction,
    isOwnerSameAsInsured: boolean
  ): AccountCenterInteractedChangeOfAddressSegmentProperties {
    const defaultProperties = AgentSiteSegmentUtils.createDefaultSegmentProperties(controller as AgentController);
    return {
      ...defaultProperties,
      action: action,
      isOwnerSameAsInsured: isOwnerSameAsInsured,
    };
  }

  static createIsHelpfulEmailSurveyResponseProperties(
    controller: CoverpathControllerInterface,
    action: SegmentEventAction,
    emailKey: string,
    response: string
  ): EmailSurveyResponseProperties {
    const defaultProperties = AgentSiteSegmentUtils.createDefaultSegmentProperties(controller as AgentController);
    return {
      ...defaultProperties,
      action: action,
      emailKey: emailKey,
      response: response,
    };
  }
}
